import { useMemo } from 'react';
import { ff } from '@atlassian/jira-feature-flagging';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import { isSubtaskHierarchyLevel } from '@atlassian/jira-issue-type-hierarchies';
import { timeTrackingFormatter } from '@atlassian/jira-time-tracking-formatter';
import { useFormatStoryPointsTotal } from '../../../../common/hooks/use-format-story-points-total';
import { ORIGINAL_ESTIMATE } from '../../../../model/constants';
import type { Card, CardId } from '../../../../model/types';
import { useBacklogSelector } from '../../../../state';
import { getTimeTrackingOptions, isCmpKanbanProject } from '../../../../state/selectors/app';
import { getCards, getFilteredOrderedCardIdsByCardListId } from '../../../../state/selectors/cards';
import { getEntities } from '../../../../state/selectors/entities';
import { getEstimationType } from '../../../../state/selectors/estimation';
import type { State } from '../../../../state/types';

interface StatisticsParams {
	cardListId: string;
	dropIndicator?: {
		droppingOverCardId: CardId;
		droppingOverCardListId: string;
		edge: 'top' | 'bottom';
	};
}

/**
 * Gets the card list IDs that this divider references.
 *
 * If it's not being dragged those are all the cards on the divider's list.
 *
 * If the divider is being dragged up, those are all cards above the position
 * the divider is being dropped at.
 *
 * If the divider is being dragged down, those are all cards on the divider's
 * list plus all cards on the target list that are above the position the divider
 * is being dropped at.
 */
export function getDividerCardIds(
	state: State,
	{ cardListId, dropIndicator }: StatisticsParams,
): CardId[] {
	const cardIds = getEntities(state).cardOrder[cardListId] ?? [];

	if (!dropIndicator) {
		return cardIds;
	}

	const cardListOrder = getEntities(state).cardListOrder ?? [];
	const filteredCards = getFilteredOrderedCardIdsByCardListId(state)[cardListId] ?? [];
	const targetListIndex = cardListOrder.indexOf(dropIndicator.droppingOverCardListId);
	const sourceListIndex = cardListOrder.indexOf(cardListId);

	// When dragging up into a previous list
	if (targetListIndex < sourceListIndex) {
		return cardIds.filter((cardId) => filteredCards.includes(cardId) === false);
	}

	if (dropIndicator.droppingOverCardListId === cardListId) {
		const { droppingOverCardId, edge } = dropIndicator;
		const targetIndex = filteredCards?.indexOf(droppingOverCardId);
		const limitIndex = edge === 'bottom' ? targetIndex + 1 : targetIndex;
		const cardsToInclude = new Set(filteredCards.slice(0, limitIndex));
		const filteredCardsSet = new Set(filteredCards);
		return cardIds.filter(
			(cardId) => filteredCardsSet.has(cardId) === false || cardsToInclude.has(cardId),
		);
	}

	const { droppingOverCardId, edge } = dropIndicator;
	const targetListCardIds =
		getFilteredOrderedCardIdsByCardListId(state)[dropIndicator.droppingOverCardListId] ?? [];
	const targetIndex = targetListCardIds?.indexOf(droppingOverCardId);
	const limitIndex = edge === 'bottom' ? targetIndex + 1 : targetIndex;
	const extraCards = targetListCardIds.slice(0, limitIndex);
	const betweenListsCardIds = cardListOrder
		.slice(sourceListIndex + 1, targetListIndex)
		.flatMap((listId) => getFilteredOrderedCardIdsByCardListId(state)[listId]);
	return cardIds.concat(betweenListsCardIds).concat(extraCards);
}

export function getTotalIssues(
	state: State,
	{ cardListId, dropIndicator }: StatisticsParams,
): number {
	const dividerCardIds = getDividerCardIds(state, {
		cardListId,
		dropIndicator,
	});

	const cards = dividerCardIds.map((cardId) => getEntities(state).card[cardId]);
	const dividerCardIdsExcludingSubtasks = cards
		.filter((card) => !isSubtaskHierarchyLevel(card.typeHierarchyLevel))
		.map((card) => card.id);

	return dividerCardIdsExcludingSubtasks.length;
}

export const useFilteredIssueCount = (cardListId: string) => {
	const cardIds =
		useBacklogSelector((state) => getFilteredOrderedCardIdsByCardListId(state)[cardListId]) || [];

	const allCards = useBacklogSelector(getCards);
	const cardIdsExcludingSubtasks = cardIds.filter((cardId) => {
		const card = allCards[cardId];
		return !isSubtaskHierarchyLevel(card.typeHierarchyLevel);
	});

	return cardIdsExcludingSubtasks?.length ?? 0;
};
export interface CardEstimates {
	storyPoints: number;
	originalTimeEstimate: number;
	remainingTimeEstimate: number;
}

export function getCardRemainingEstimates(card: Card): CardEstimates {
	if (card.isDone) {
		return { storyPoints: 0, originalTimeEstimate: 0, remainingTimeEstimate: 0 };
	}
	return {
		storyPoints: card.estimate?.storyPoints ?? 0,
		originalTimeEstimate: card.estimate?.originalEstimate?.value ?? 0,
		remainingTimeEstimate: card.estimate?.timeRemainingEstimate?.value ?? 0,
	};
}

export type CardListEstimates = CardEstimates & {
	totalIssues: number;
};

export function getRemainingEstimate(
	state: State,
	{ cardListId, dropIndicator }: StatisticsParams,
): CardListEstimates {
	const cardIds = getDividerCardIds(state, { cardListId, dropIndicator });
	let cards = cardIds.map((cardId) => getEntities(state).card[cardId]);

	if (
		!ff('jsw.disable-tracking-statistic-parent-aggregation.tnk1073') &&
		!isCmpKanbanProject(state)
	) {
		cards = cards.filter((card) => !isSubtaskHierarchyLevel(card.typeHierarchyLevel));
	}

	const estimates = cards.map((card) => getCardRemainingEstimates(card));

	return estimates.reduce(
		(acc: CardListEstimates, { storyPoints, originalTimeEstimate, remainingTimeEstimate }) => ({
			totalIssues: acc.totalIssues + 1,
			storyPoints: acc.storyPoints + storyPoints,
			originalTimeEstimate: acc.originalTimeEstimate + originalTimeEstimate,
			remainingTimeEstimate: acc.remainingTimeEstimate + remainingTimeEstimate,
		}),
		{
			storyPoints: 0,
			originalTimeEstimate: 0,
			remainingTimeEstimate: 0,
			totalIssues: 0,
		} as const,
	);
}

export function useFilteredRemainingEstimate(cardListId: string): CardListEstimates {
	const cardIds = useBacklogSelector(
		(state) => getFilteredOrderedCardIdsByCardListId(state)[cardListId] ?? [],
	);
	const allCards = useBacklogSelector(getCards);
	const isCmpKanban = useBacklogSelector(isCmpKanbanProject);

	return useMemo(() => {
		let cards = cardIds.map((cardId) => allCards[cardId]);
		if (!ff('jsw.disable-tracking-statistic-parent-aggregation.tnk1073') && !isCmpKanban) {
			cards = cards.filter((card) => !isSubtaskHierarchyLevel(card.typeHierarchyLevel));
		}
		const estimates = cards.map((card) => getCardRemainingEstimates(card));
		return estimates.reduce(
			(acc: CardListEstimates, { storyPoints, originalTimeEstimate, remainingTimeEstimate }) => ({
				totalIssues: acc.totalIssues + 1,
				storyPoints: acc.storyPoints + storyPoints,
				originalTimeEstimate: acc.originalTimeEstimate + originalTimeEstimate,
				remainingTimeEstimate: acc.remainingTimeEstimate + remainingTimeEstimate,
			}),
			{
				storyPoints: 0,
				originalTimeEstimate: 0,
				remainingTimeEstimate: 0,
				totalIssues: 0,
			} as const,
		);
	}, [cardIds, allCards, isCmpKanban]);
}

export function useFormatEstimationValue() {
	const { formatMessage } = useIntl();
	const timeTrackingOptions = useBacklogSelector(getTimeTrackingOptions);
	const estimationType = useBacklogSelector(getEstimationType);
	const formatStoryPointsTotal = useFormatStoryPointsTotal();

	return (value: number): string => {
		if (estimationType === ORIGINAL_ESTIMATE) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return timeTrackingFormatter(value, timeTrackingOptions, {
				formatMessage,
			} as IntlShape);
		}

		return formatStoryPointsTotal(value);
	};
}

export const useFormatTimeTrackingValue = () => {
	const { formatMessage } = useIntl();
	const timeTrackingOptions = useBacklogSelector(getTimeTrackingOptions);

	return (value: number): string =>
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		timeTrackingFormatter(value, timeTrackingOptions, { formatMessage } as IntlShape);
};
